import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const BulkUpload = ({
  title,
  downloadLabel,
  uploadLabel,
  columns,
  sampleRow,
  downloadData = [],
  onUpload,
  validateRow
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successCount, setSuccessCount] = useState(0);
  const [progress, setProgress] = useState(0);

  // Helper function to convert Excel date serial number to date string
  const excelDateToDateString = (value) => {
    if (!value) return '';
    
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.split(' ')[0];
      }
      return value;
    }
    
    if (typeof value === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + value * 86400000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return String(value);
  };

  // Helper function to format any date-like value
  const formatDateValue = (value) => {
    if (!value) return '';
    
    if (typeof value === 'string') {
      if (value.includes('-') || value.includes('/')) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
      return value;
    }
    
    if (typeof value === 'number') {
      return excelDateToDateString(value);
    }
    
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return String(value);
  };

  // THE CORRECTED handleFile FUNCTION (ONLY ONE)
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      toast.warning('Please select a file to upload.');
      return;
    }
    
    setError('');
    setSuccessCount(0);
    setProgress(0);
    setUploading(true);

    try {
      // Read file as array buffer
      const buffer = await file.arrayBuffer();
      const data = new Uint8Array(buffer);
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // Convert to JSON with raw values
      const json = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        defval: '',
        raw: true
      });

      if (json.length < 2) {
        const errorMsg = 'Excel file is empty or has no data rows.';
        setError(errorMsg);
        toast.error(`❌ ${errorMsg}`);
        setUploading(false);
        return;
      }

      const headers = json[0];
      const requiredHeaders = columns.map((c) => c.label);
      const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));
      if (missingHeaders.length > 0) {
        const errorMsg = `Missing columns: ${missingHeaders.join(', ')}`;
        setError(errorMsg);
        toast.error(`❌ ${errorMsg}`);
        setUploading(false);
        return;
      }

      const headerIndexes = headers.reduce((acc, h, i) => {
        acc[h] = i;
        return acc;
      }, {});

      // Process all rows
      const allRows = json
        .slice(1)
        .filter((row) => row.some((cell) => cell !== undefined && cell !== '' && cell !== null));

      if (allRows.length === 0) {
        const errorMsg = 'No data rows found in the Excel file.';
        setError(errorMsg);
        toast.warning(`⚠️ ${errorMsg}`);
        setUploading(false);
        return;
      }

      // Parse all rows
      const parsed = allRows.map((row) => {
        const obj = {};
        columns.forEach((col) => {
          const idx = headerIndexes[col.label];
          if (idx !== undefined) {
            let value = row[idx];
            
            if (col.key === 'purchaseDate' || 
                col.key === 'installationDate' || 
                col.key === 'installaionWarrantyend') {
              value = formatDateValue(value);
            } else {
              value = value !== undefined && value !== null ? String(value).trim() : '';
            }
            
            obj[col.key] = value;
          } else {
            obj[col.key] = '';
          }
        });
        return obj;
      });

      console.log(`📊 Total rows parsed: ${parsed.length}`);

      // Validate with chunking if validation function exists
      if (validateRow) {
        const allErrors = [];
        const validRows = [];
        const chunkSize = 50;
        
        // Show progress toast
        const progressToast = toast.info(`Processing ${parsed.length} rows...`, {
          autoClose: false,
          hideProgressBar: false,
        });

        for (let i = 0; i < parsed.length; i += chunkSize) {
          const chunk = parsed.slice(i, i + chunkSize);
          
          const progressPercent = Math.round((i / parsed.length) * 100);
          setProgress(progressPercent);
          toast.update(progressToast, {
            render: `Processing rows ${i + 1} to ${Math.min(i + chunkSize, parsed.length)} of ${parsed.length} (${progressPercent}%)`,
          });

          chunk.forEach((row, index) => {
            const rowNumber = i + index + 2;
            const rowErrors = validateRow(row, index, parsed);
            if (rowErrors && rowErrors.length > 0) {
              rowErrors.forEach((err) => allErrors.push({ row: rowNumber, ...err }));
            } else {
              validRows.push(row);
            }
          });

          // Allow UI to update between chunks
          await new Promise(resolve => setTimeout(resolve, 0));
        }

        // Close progress toast
        toast.dismiss(progressToast);
        setProgress(100);

        console.log(`✅ Valid rows: ${validRows.length}, ❌ Error rows: ${allErrors.length}`);
        
        // If there are errors, show them
        if (allErrors.length > 0) {
          const errorMessages = allErrors
            .map((e) => `Row ${e.row}, Column "${e.column}": ${e.message}`)
            .join('\n');
          setError(errorMessages);
          
          const errorCount = allErrors.length;
          toast.error(`❌ ${errorCount} error(s) found in the template. Please fix and try again.`);
          
          console.error('Bulk upload validation errors:');
          allErrors.forEach((e, i) => {
            console.error(`  ${i + 1}. Row ${e.row}, Column "${e.column}": ${e.message}`);
          });
          
          setUploading(false);
          setProgress(0);
          return;
        }
        
        // Use only valid rows for upload
        if (validRows.length === 0) {
          toast.warning('⚠️ No valid rows to upload.');
          setUploading(false);
          setProgress(0);
          return;
        }
        
        // Upload valid rows in chunks
        try {
          const uploadChunkSize = 50;
          let uploadedCount = 0;
          
          const uploadProgressToast = toast.info(`Uploading ${validRows.length} rows...`, {
            autoClose: false,
            hideProgressBar: false,
          });

          for (let i = 0; i < validRows.length; i += uploadChunkSize) {
            const chunk = validRows.slice(i, i + uploadChunkSize);
            
            const progressPercent = Math.round(((i + chunk.length) / validRows.length) * 100);
            setProgress(progressPercent);
            toast.update(uploadProgressToast, {
              render: `Uploading ${Math.min(i + uploadChunkSize, validRows.length)} of ${validRows.length} rows...`,
            });

            await onUpload(chunk);
            uploadedCount += chunk.length;

            await new Promise(resolve => setTimeout(resolve, 100));
          }

          toast.dismiss(uploadProgressToast);
          setSuccessCount(uploadedCount);
          setError('');
          setProgress(100);
          
          toast.success(`✅ ${uploadedCount} ${title.toLowerCase()}(s) uploaded successfully!`);
        } catch (uploadError) {
          const errorMsg = uploadError.message || 'Failed to upload data.';
          setError(errorMsg);
          toast.error(`❌ Upload failed: ${errorMsg}`);
          console.error('Upload error:', uploadError);
        }
      } else {
        // No validation function provided, upload all rows in chunks
        try {
          const uploadChunkSize = 50;
          let uploadedCount = 0;
          
          const uploadProgressToast = toast.info(`Uploading ${parsed.length} rows...`, {
            autoClose: false,
            hideProgressBar: false,
          });

          for (let i = 0; i < parsed.length; i += uploadChunkSize) {
            const chunk = parsed.slice(i, i + uploadChunkSize);
            
            const progressPercent = Math.round(((i + chunk.length) / parsed.length) * 100);
            setProgress(progressPercent);
            toast.update(uploadProgressToast, {
              render: `Uploading ${Math.min(i + uploadChunkSize, parsed.length)} of ${parsed.length} rows...`,
            });

            await onUpload(chunk);
            uploadedCount += chunk.length;

            await new Promise(resolve => setTimeout(resolve, 100));
          }

          toast.dismiss(uploadProgressToast);
          setSuccessCount(uploadedCount);
          setError('');
          setProgress(100);
          toast.success(`✅ ${uploadedCount} ${title.toLowerCase()}(s) uploaded successfully!`);
        } catch (uploadError) {
          const errorMsg = uploadError.message || 'Failed to upload data.';
          setError(errorMsg);
          toast.error(`❌ Upload failed: ${errorMsg}`);
          console.error('Upload error:', uploadError);
        }
      }
    } catch (err) {
      const errorMsg = 'Failed to parse Excel file. Please check the file format.';
      setError(errorMsg);
      toast.error(`❌ ${errorMsg}`);
      console.error('File parsing error:', err);
    }
    setUploading(false);
    setProgress(0);
    
    // Reset file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    try {
      const wb = XLSX.utils.book_new();

      let dataToExport = [];

      if (downloadData && downloadData.length > 0) {
        dataToExport = downloadData.map((row) => {
          const formatted = {};
          columns.forEach((col) => {
            formatted[col.label] = row[col.key] ?? '';
          });
          return formatted;
        });
      } else {
        const sample = {};
        columns.forEach((col) => {
          sample[col.label] = sampleRow[col.key] ?? '';
        });
        dataToExport = [sample];
      }

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      XLSX.utils.book_append_sheet(wb, ws, 'Template');
      XLSX.writeFile(wb, `${title}.xlsx`);
      
      toast.info(`📄 Template downloaded as "${title}.xlsx"`);
    } catch (err) {
      toast.error('❌ Failed to download template.');
      console.error('Template download error:', err);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        onClick={downloadTemplate}
        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-xl text-sm transition-colors"
      >
        {downloadLabel}
      </button>
      <label className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm cursor-pointer transition-colors">
        {uploading ? (
          <span className="flex items-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Uploading...
          </span>
        ) : uploadLabel}
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFile}
          ref={fileInputRef}
          className="hidden"
          disabled={uploading}
        />
      </label>
      
      {/* Progress bar */}
      {uploading && progress > 0 && progress < 100 && (
        <div className="w-32">
          <div className="bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="text-xs text-gray-500">{progress}%</span>
        </div>
      )}
      
      {error && (
        <span className="text-red-600 text-sm whitespace-pre-line max-w-md overflow-hidden text-ellipsis">
          ⚠️ {error.split('\n').length > 3 ? `${error.split('\n').slice(0, 3).join('\n')}...` : error}
        </span>
      )}
      {successCount > 0 && !error && (
        <span className="text-green-600 text-sm">✅ {successCount} records uploaded.</span>
      )}
    </div>
  );
};

export default BulkUpload;